// ════════════════════════════════════════════════════════════════════════════
// LpaInput — input primitivo, controlado ou não, dual-mode, a11y
// Use junto com <LpaField /> para auto-conformidade WCAG (label + aria).
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

type NativeProps = React.InputHTMLAttributes<HTMLInputElement>;

export interface LpaInputProps extends Omit<NativeProps, "size" | "onChange"> {
  /** Tamanho visual */
  size?: "sm" | "md" | "lg";
  /** Estado de erro (controlado externamente — geralmente vem do LpaField) */
  invalid?: boolean;
  /** Largura full container */
  fullWidth?: boolean;
  /** onChange controlado simples (recebe direto o valor) */
  onChange?: (value: string) => void;
  /** Para retrocompat: aceita também onChange nativo via onValueChange */
  onValueChange?: (value: string) => void;
  testId?: string;
}

const SIZE_PADDING: Record<NonNullable<LpaInputProps["size"]>, string> = {
  sm: "5px 9px",
  md: "8px 10px",
  lg: "10px 12px",
};

const SIZE_FONT: Record<NonNullable<LpaInputProps["size"]>, string> = {
  sm: "var(--lpa-text-sm)",
  md: "var(--lpa-text-lg)",
  lg: "var(--lpa-text-xl)",
};

const LpaInput = React.forwardRef<HTMLInputElement, LpaInputProps>(function LpaInput(
  {
    size = "md",
    invalid = false,
    fullWidth = true,
    onChange,
    onValueChange,
    type = "text",
    style,
    className,
    testId,
    ...rest
  },
  ref
) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
    onValueChange?.(e.target.value);
  };

  return (
    <input
      ref={ref}
      type={type}
      onChange={handleChange}
      data-testid={testId}
      aria-invalid={invalid || undefined}
      className={className}
      style={{
        width: fullWidth ? "100%" : undefined,
        padding: SIZE_PADDING[size],
        fontSize: SIZE_FONT[size],
        fontFamily: "var(--lpa-font-sans)",
        color: "var(--lpa-text)",
        background: "var(--lpa-bg)",
        border: `1px solid ${invalid ? "var(--lpa-danger)" : "var(--lpa-border)"}`,
        borderRadius: "var(--lpa-radius-md)",
        outline: "none",
        transition: "border-color var(--lpa-tr-fast), box-shadow var(--lpa-tr-fast)",
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = invalid ? "var(--lpa-danger)" : "var(--lpa-accent)";
        e.currentTarget.style.boxShadow = invalid
          ? "0 0 0 3px var(--lpa-danger-bg)"
          : "0 0 0 3px var(--lpa-accent-soft)";
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = invalid ? "var(--lpa-danger)" : "var(--lpa-border)";
        e.currentTarget.style.boxShadow = "none";
        rest.onBlur?.(e);
      }}
      {...rest}
    />
  );
});

export default LpaInput;
